// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Component imports
import { Button, Col, Grid, Row } from 'antd';
import SEO from '../components/SEO/SEO';

// Misc imports
import api from '../api';
import { NAV_NDES, NAV_NDES_CREATE, NAV_RELATIVE_NDES_BASIC_DETAILS, NAV_STATISTICS, NAV_ABOUT } from '../constants';
import mainImg from '../assets/images/landing2.png';
import aboutImg from '../assets/images/us2.png';
import menuImg from '../assets/images/menu-img.png';

// ── Feature cards data ──────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
    title: 'Curated Archive',
    body: 'Every account is sourced from verified publications and submitted directly by those who lived it.'
  },
  {
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4l3 3" />
      </svg>
    ),
    title: 'Data-Driven Insights',
    body: 'Natural Language Understanding transforms raw narratives into structured data, revealing patterns across thousands of accounts.'
  },
  {
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
    title: 'Anonymous & Safe',
    body: 'Anonymity removes the stigma barrier. Negative and distressing NDEs can finally be shared freely.'
  }
];

// ── Styles ───────────────────────────────────────────────────────────────────

const GLOW_STYLE = { display: 'none' };
const GLOW2_STYLE = { display: 'none' };

const CARD_STYLE = {
  background: '#fff',
  borderRadius: 16,
  padding: '36px 32px',
  height: '100%',
  border: '1px solid #eef0ff',
  boxShadow: '0 2px 16px rgba(145,149,252,0.08)'
};

const ICON_WRAPPER = {
  width: 60,
  height: 60,
  borderRadius: 14,
  background: 'linear-gradient(135deg, #9195fc22 0%, #9195fc44 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#9195fc',
  marginBottom: 20
};

const STAT_CARD_STYLE = {
  background: '#fff',
  borderRadius: 16,
  padding: '24px 16px',
  textAlign: 'center',
  border: '1px solid #eee'
};

const { useBreakpoint } = Grid;

const HomeScreen = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.fetchStatistics().then(data => {
      if (data) {
        const typeDist = data.typeDistribution || [];
        const total = data.totalCount || 0;
        const peaceful = typeDist.find(d => d.label === 'peaceful')?.value || 0;
        const distressing = typeDist.find(d => d.label === 'distressing')?.value || 0;
        const mixed = typeDist.find(d => d.label === 'mixed')?.value || 0;
        // Use the sum of typed entries as denominator (matches the stats page),
        // since totalCount includes private NDEs and NDEs without a type set.
        const typedTotal = typeDist.reduce((sum, d) => sum + d.value, 0);
        setStats({
          total,
          peacefulPct: typedTotal > 0 ? (peaceful / typedTotal) * 100 : null,
          distressingPct: typedTotal > 0 ? (distressing / typedTotal) * 100 : null,
          mixedPct: typedTotal > 0 ? (mixed / typedTotal) * 100 : null
        });
      }
    }).catch(() => {});
  }, []);

  return (
    <Fragment>
      <SEO
        path="/"
        description="The largest freely accessible database of verified near-death experiences (NDEs). Browse thousands of documented accounts, contribute your own NDE anonymously, and discover insights through data science and structured analysis."
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          // Same @id as the static node in public/index.html so the two are
          // understood as one entity rather than two competing WebSite nodes.
          '@id': `${process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'}/#website`,
          name: 'NDE Archive',
          // Teaches search engines that the one-word "NDEArchive" is this site.
          alternateName: ['NDEArchive', 'ndearchive', 'ndearchive.com'],
          description: 'The largest freely accessible database of verified near-death experiences (NDEs) analyzed through data science and natural language understanding.',
          url: process.env.REACT_APP_SITE_URL || 'https://ndearchive.com',
          publisher: { '@id': `${process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'}/#organization` },
          inLanguage: 'en',
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'}/ndes?q={search_term_string}`
            },
            'query-input': 'required name=search_term_string'
          }
        }}
      />

      {/* ── Hero ── */}
      <div style={{ padding: isMobile ? '24px 8px 32px' : '56px 8px 56px 8px', marginBottom: isMobile ? 24 : 48 }}>
        <div style={GLOW_STYLE} />
        <div style={GLOW2_STYLE} />
        <Row gutter={[48, 40]} align="middle" justify="space-between">
          <Col xs={24} md={16} lg={14} style={{ position: 'relative', zIndex: 1 }}>
            <p style={{ color: '#9195fc', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', fontSize: 13, marginBottom: 20 }}>
              Near-Death Experience Database
            </p>
            <h1 style={{
              fontSize: 'clamp(34px, 4vw, 50px)',
              lineHeight: 1.1,
              color: '#1a1a2e',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 800,
              letterSpacing: '-1.5px',
              marginBottom: 24
            }}>
              Near-death experiences:<br />
              <span style={{ color: '#9195fc' }}>what the data reveals.</span>
            </h1>
            <p style={{ fontSize: 18, lineHeight: '30px', color: '#555', marginBottom: 36 }}>
              Welcome to the <strong>NDE Archive</strong>, a comprehensive database of verified near-death experiences (NDEs) found across the web.
              <br /><br />
              The mission is to provide a platform for people to <strong>explore and learn</strong> from these profound encounters with the unknown, and to promote a better understanding of life, death, and the human experience.
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <Button
                type="primary"
                size="large"
                style={{ fontSize: 16, borderRadius: 10, height: 52, fontWeight: 600, paddingLeft: 28, paddingRight: 28 }}
                onClick={() => navigate(NAV_NDES)}
              >
                Browse experiences
              </Button>
              <Button
                size="large"
                style={{
                  fontSize: 16,
                  borderRadius: 10,
                  height: 52,
                  fontWeight: 600,
                  paddingLeft: 28,
                  paddingRight: 28
                }}
                onClick={() => navigate(NAV_NDES_CREATE + NAV_RELATIVE_NDES_BASIC_DETAILS)}
              >
                Share yours
              </Button>
            </div>
          </Col>
          <Col xs={0} md={8} lg={10} style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <img
              src={mainImg}
              alt="Illustration representing near-death experience research"
              style={{ maxHeight: 520, filter: 'drop-shadow(0 16px 48px rgba(145,149,252,0.3))' }}
            />
          </Col>
        </Row>
      </div>

      {/* ── Stats strip ── */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)', gap: 16, marginBottom: 48 }}>
          {stats.total != null && (
            <div style={STAT_CARD_STYLE}>
              <div style={{ fontSize: isMobile ? 26 : 36, fontWeight: 800, lineHeight: 1, marginBottom: 6, color: '#6366f9' }}>{stats.total.toLocaleString()}</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#777' }}>Experiences archived</div>
            </div>
          )}
          {stats.peacefulPct != null && (
            <div style={STAT_CARD_STYLE}>
              <div style={{ fontSize: isMobile ? 26 : 36, fontWeight: 800, lineHeight: 1, marginBottom: 6, color: '#1a9e83' }}>{Math.round(stats.peacefulPct)}%</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#777' }}>Peaceful</div>
            </div>
          )}
          {stats.distressingPct != null && (
            <div style={STAT_CARD_STYLE}>
              <div style={{ fontSize: isMobile ? 26 : 36, fontWeight: 800, lineHeight: 1, marginBottom: 6, color: '#c0415a' }}>{Math.round(stats.distressingPct)}%</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#777' }}>Distressing</div>
            </div>
          )}
          {stats.mixedPct != null && (
            <div style={STAT_CARD_STYLE}>
              <div style={{ fontSize: isMobile ? 26 : 36, fontWeight: 800, lineHeight: 1, marginBottom: 6, color: '#f59e0b' }}>{Math.round(stats.mixedPct)}%</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#777' }}>Neutral or mixed</div>
            </div>
          )}
          <div
            style={{ ...STAT_CARD_STYLE, cursor: 'pointer', gridColumn: isMobile ? 'span 2' : undefined }}
            onClick={() => navigate(NAV_STATISTICS)}
          >
            <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, lineHeight: 1, marginBottom: 6, color: '#6366f9' }}>↗</div>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#777' }}>See full statistics</div>
          </div>
        </div>
      )}

      {/* ── Feature cards ── */}
      {!isMobile && <Row gutter={[24, 24]} style={{ marginBottom: 48 }}>
        {FEATURES.map((f) => (
          <Col xs={24} md={8} key={f.title}>
            <div style={CARD_STYLE}>
              <div style={ICON_WRAPPER}>{f.icon}</div>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>{f.title}</h3>
              <p style={{ fontSize: 15, lineHeight: '26px', color: '#555', margin: 0 }}>{f.body}</p>
            </div>
          </Col>
        ))}
      </Row>}

      {/* ── About section ── */}
      <Row gutter={[48, 40]} align="middle" justify="space-between" style={{ marginBottom: 64 }}>
        <Col xs={24} md={10} lg={10} style={{ textAlign: 'center' }}>
          <img
            src={aboutImg}
            alt="The NDE Archive team"
            style={{ maxWidth: '100%', maxHeight: 420, filter: 'drop-shadow(0 4px 24px rgba(145,149,252,0.12))' }}
          />
        </Col>
        <Col xs={24} md={14} lg={13}>
          <p style={{ color: '#9195fc', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', fontSize: 13, marginBottom: 12 }}>About the project</p>
          <h2 style={{ fontSize: 'clamp(28px, 3vw, 40px)', lineHeight: 1.2, fontWeight: 800, color: '#1a1a2e', marginBottom: 20 }}>
            A passion project built on curiosity and openness
          </h2>
          <p style={{ fontSize: 18, lineHeight: '30px', color: '#555', marginBottom: 36 }}>
            The NDE Archive is a free, community-driven platform that collects near-death experience accounts from verified sources across the internet. Users are encouraged to <strong>contribute their own experiences anonymously</strong>, to promote complete openness and honesty. This is especially for negative or distressing NDEs, which are often underreported due to stigma.
            <br /><br />
            By applying <strong>data science and Natural Language Understanding</strong> to thousands of accounts, the platform surfaces patterns that would be invisible to any single reader, thus giving researchers, the curious, and survivors alike a richer picture of what the data reveals.
          </p>
        </Col>
      </Row>

      {/* ── Bottom CTA ── */}
      <div style={{
        background: 'linear-gradient(135deg, #5b54d6 0%, #9195fc 100%)',
        borderRadius: 16,
        padding: '52px 48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 24,
        marginBottom: 48,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <img
          src={menuImg}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: -60,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 420,
            opacity: 0.1,
            pointerEvents: 'none',
            userSelect: 'none'
          }}
        />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: '#fff', marginBottom: 8 }}>Have you had a near-death experience?</h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.72)', margin: 0 }}>Your story matters, and you can share it completely anonymously.</p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
          <Button
            size="large"
            style={{ fontSize: 15, borderRadius: 10, height: 48, fontWeight: 600, background: '#9195fc', color: '#fff', border: 'none' }}
            onClick={() => navigate(NAV_NDES_CREATE + NAV_RELATIVE_NDES_BASIC_DETAILS)}
          >
            Share your experience
          </Button>
          <Button
            size="large"
            style={{ fontSize: 15, borderRadius: 10, height: 48, fontWeight: 600, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.2)' }}
            onClick={() => navigate(NAV_ABOUT)}
          >
            Learn about us
          </Button>
        </div>
      </div>
    </Fragment>
  );
};

export default HomeScreen;
